import React, { useEffect, useRef, useState } from "react";
import { parseISTTimestamp } from "../utils/dateTime";

const BASE_URL = "/v1";

function BloodTestModal({ show, onClose, pet }) {
    const [allBloodTests, setAllBloodTests] = useState([]);
    const [bloodSearch, setBloodSearch] = useState("");
    const [bloodSuggestions, setBloodSuggestions] = useState([]);
    const [selectedBloodTests, setSelectedBloodTests] = useState([]);
    const [loadingSuggested, setLoadingSuggested] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const searchRef = useRef(null);
    const customIdRef = useRef(1);

    const normalizeTest = (test, fallback = {}) => ({
        key: test.key || `${test.id || fallback.testId || "test"}-${Math.random().toString(36).slice(2, 9)}`,
        testId: test.testId ?? test.id ?? fallback.testId ?? null,
        testName:
            test.testName ||
            test.profileParticulars ||
            fallback.testName ||
            "",
        category: test.category || fallback.category || "",
        machineType: test.machineType || fallback.machineType || "",
        specimenRequired: test.specimenRequired || fallback.specimenRequired || "",
        actualPrice: test.actualPrice ?? fallback.actualPrice ?? "",
        source: test.source || fallback.source || "existing"
    });

    const fetchClinicTests = async () => {
        try {
            const token = localStorage.getItem("token");

            const res = await fetch(`${BASE_URL}/labTest/clinic`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) return;

            const data = await res.json();
            setAllBloodTests(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchTestDetails = async (testId) => {
        const token = localStorage.getItem("token");
        const res = await fetch(`${BASE_URL}/labTest/${testId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) return null;

        return res.json();
    };

    const fetchSuggestedTests = async () => {
        if (!pet?.id) {
            setSelectedBloodTests([]);
            return;
        }

        try {
            setLoadingSuggested(true);
            setError("");
            setMessage("");

            const token = localStorage.getItem("token");
            const res = await fetch(`${BASE_URL}/pets/visits/${pet.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!res.ok) {
                setSelectedBloodTests([]);
                return;
            }

            const data = await res.json();
            const visits = Array.isArray(data)
                ? [...data].sort((a, b) =>
                    parseISTTimestamp(b.createdAt || b.visitDate) -
                    parseISTTimestamp(a.createdAt || a.visitDate)
                )
                : [];

            const latestVisit = visits[0];
            const latestBloodTests = latestVisit?.bloodTest || latestVisit?.bloodTests || [];

            if (!latestBloodTests.length) {
                setSelectedBloodTests([]);
                return;
            }

            const enriched = await Promise.all(
                latestBloodTests.map(async (test) => {
                    const details = test.testId ? await fetchTestDetails(test.testId) : null;
                    return normalizeTest(
                        {
                            ...test,
                            ...details,
                            key: `suggested-${test.id || test.testId}`
                        },
                        {
                            source: "suggested",
                            actualPrice: details?.actualPrice ?? "",
                            category: details?.category || "",
                            machineType: details?.machineType || "",
                            specimenRequired: details?.specimenRequired || ""
                        }
                    );
                })
            );

            setSelectedBloodTests(enriched);
        } catch (err) {
            console.error(err);
            setError("Unable to load suggested lab tests");
        } finally {
            setLoadingSuggested(false);
        }
    };

    useEffect(() => {
        if (!show) return;

        setBloodSearch("");
        setBloodSuggestions([]);
        setMessage("");
        setError("");
        customIdRef.current = 1;

        fetchClinicTests();
        fetchSuggestedTests();
    }, [show, pet?.id]);

    if (!show) return null;

    const handleBloodSearch = (value) => {
        setBloodSearch(value);

        if (!value || value.length < 2) {
            setBloodSuggestions([]);
            return;
        }

        const filtered = allBloodTests.filter((b) =>
            b.profileParticulars?.toLowerCase().includes(value.toLowerCase()) ||
            b.category?.toLowerCase().includes(value.toLowerCase())
        );

        setBloodSuggestions(filtered.slice(0, 10));
    };

    const addExistingTest = (test) => {
        const exists = selectedBloodTests.some((x) =>
            x.testId === test.id || x.testName === test.profileParticulars
        );

        if (exists) return;

        setSelectedBloodTests((prev) => [
            ...prev,
            normalizeTest(
                {
                    key: `existing-${test.id}`,
                    testId: test.id,
                    testName: test.profileParticulars,
                    category: test.category,
                    machineType: test.machineType,
                    specimenRequired: test.specimenRequired,
                    actualPrice: test.actualPrice,
                    source: "existing"
                }
            )
        ]);

        setBloodSearch("");
        setBloodSuggestions([]);
        setMessage("");
    };

    const addCustomTest = () => {
        setSelectedBloodTests((prev) => [
            ...prev,
            {
                key: `custom-${Date.now()}-${customIdRef.current++}`,
                testId: null,
                testName: "",
                category: "",
                machineType: "",
                specimenRequired: "",
                actualPrice: "",
                source: "custom"
            }
        ]);
    };

    const updateSelectedTest = (key, field, value) => {
        setSelectedBloodTests((prev) =>
            prev.map((item) =>
                item.key === key
                    ? {
                        ...item,
                        [field]: value
                    }
                    : item
            )
        );
    };

    const removeBloodTest = (key) => {
        setSelectedBloodTests((prev) => prev.filter((x) => x.key !== key));
    };

    const totalAmount = selectedBloodTests.reduce(
        (sum, item) => sum + (Number(item.actualPrice) || 0),
        0
    );

    const handleSave = () => {
            setMessage("Selected lab tests are ready.");
        setTimeout(() => {
            onClose();
        }, 300);
    };

    return (
        <>
            <div className="modal-backdrop fade show"></div>

            <div className="modal show fade d-block" tabIndex="-1">
                <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                Lab Test Booking - {pet?.name}
                            </h5>
                            <button className="btn-close" onClick={onClose}></button>
                        </div>

                        <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                            {loadingSuggested && (
                                <div className="modal-loading-overlay">
                                    <div className="text-center">
                                        <div className="spinner-border" role="status"></div>
                                        <p className="mt-2 mb-0">Loading...</p>
                                    </div>
                                </div>
                            )}

                            {message && <div className="alert alert-success py-2">{message}</div>}
                            {error && <div className="alert alert-danger py-2">{error}</div>}

                            <div className="mb-4">
                                <label className="form-label fw-semibold">
                                    Search clinic lab tests
                                </label>
                                <div style={{ position: "relative" }}>
                                    <input
                                        ref={searchRef}
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Lab Test"
                                        value={bloodSearch}
                                        onChange={(e) => handleBloodSearch(e.target.value)}
                                        autoComplete="off"
                                    />

                                    {bloodSuggestions.length > 0 && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "100%",
                                                left: 0,
                                                right: 0,
                                                background: "#fff",
                                                border: "1px solid #ddd",
                                                zIndex: 9999,
                                                maxHeight: "300px",
                                                overflowY: "auto"
                                            }}
                                        >
                                            {bloodSuggestions.map((item) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        padding: "10px",
                                                        borderBottom: "1px solid #eee",
                                                        cursor: "pointer"
                                                    }}
                                                    onClick={() => addExistingTest(item)}
                                                >
                                                    <div>
                                                        <b>{item.profileParticulars}</b>
                                                    </div>
                                                    <div className="small text-muted">
                                                        Category: {item.category || "-"}
                                                    </div>
                                                    <div className="small text-muted">
                                                        Machine: {item.machineType || "-"}
                                                    </div>
                                                    <div className="small text-muted">
                                                        Specimen: {item.specimenRequired || "-"}
                                                    </div>
                                                    <div className="small text-success">
                                                        ₹ {item.actualPrice ?? 0}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="mb-2">
                                <h6 className="mb-1">Suggested from latest diagnosis</h6>
                                <small className="text-muted">
                                    These tests were loaded from the most recent visit.
                                </small>
                            </div>

                            {selectedBloodTests.length === 0 ? (
                                <p className="text-muted mb-0">No lab tests suggested yet</p>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ minWidth: "220px" }}>Test Name</th>
                                                <th>Test ID</th>
                                                <th>Category</th>
                                                <th>Machine</th>
                                                <th>Specimen</th>
                                                <th style={{ width: "120px" }}>Price</th>
                                                <th style={{ width: "120px" }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedBloodTests.map((item) => (
                                                <tr key={item.key}>
                                                    <td>
                                                        {item.source === "custom" ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                placeholder="Enter test name"
                                                                value={item.testName}
                                                                onChange={(e) =>
                                                                    updateSelectedTest(item.key, "testName", e.target.value)
                                                                }
                                                            />
                                                        ) : (
                                                            item.testName || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.source === "custom" ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                placeholder="Test ID"
                                                                value={item.testId || ""}
                                                                onChange={(e) =>
                                                                    updateSelectedTest(item.key, "testId", e.target.value)
                                                                }
                                                            />
                                                        ) : (
                                                            item.testId ?? "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.source === "custom" ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                placeholder="Category"
                                                                value={item.category || ""}
                                                                onChange={(e) =>
                                                                    updateSelectedTest(item.key, "category", e.target.value)
                                                                }
                                                            />
                                                        ) : (
                                                            item.category || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.source === "custom" ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                placeholder="Machine Type"
                                                                value={item.machineType || ""}
                                                                onChange={(e) =>
                                                                    updateSelectedTest(item.key, "machineType", e.target.value)
                                                                }
                                                            />
                                                        ) : (
                                                            item.machineType || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.source === "custom" ? (
                                                            <input
                                                                className="form-control form-control-sm"
                                                                placeholder="Specimen Required"
                                                                value={item.specimenRequired || ""}
                                                                onChange={(e) =>
                                                                    updateSelectedTest(item.key, "specimenRequired", e.target.value)
                                                                }
                                                            />
                                                        ) : (
                                                            item.specimenRequired || "-"
                                                        )}
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="form-control form-control-sm"
                                                            value={item.actualPrice}
                                                            placeholder="0"
                                                            onChange={(e) =>
                                                                updateSelectedTest(item.key, "actualPrice", e.target.value)
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => removeBloodTest(item.key)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th colSpan="5" className="text-end">
                                                    Total
                                                </th>
                                                <th>₹ {totalAmount.toFixed(2)}</th>
                                                <th></th>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                            <button className="btn btn-outline-secondary" onClick={addCustomTest}>
                                + Add New Test
                            </button>
                            <button className="btn btn-primary" onClick={handleSave}>
                                Save Lab Test
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default BloodTestModal;
